import { useEffect, useState, type FormEvent } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../lib/auth';
import { formatClock, formatLongDate, getGreeting } from '../lib/time';

/**
 * Sign in.
 *
 * Layout and colours follow icu-gps's LoginPage (frontend/src/login/LoginPage.jsx):
 * a 40% form pane with the logo, greeting and live clock stacked above the form,
 * and a 60% branding pane on a 135° background-to-primary gradient, hidden below
 * `md`. Staff sign in with Supabase Auth accounts created from invites.
 */

// icu-gps's hard-coded greys, kept identical so the two products read as one family.
const TEXT_LIGHT = '#ecf0f1';
const TEXT_MUTED = '#95a5a6';
const BORDER = '#7f8c8d';

const fieldLabelSx: SxProps<Theme> = {
    color: 'primary.main',
    fontSize: '0.75rem',
    display: 'block',
    mb: 1,
};

const textFieldSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': { bgcolor: 'background.default', color: TEXT_LIGHT, borderRadius: '8px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: TEXT_MUTED },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '& .MuiInputBase-input': { color: TEXT_LIGHT },
};

export function Login(): React.JSX.Element {
    const { signIn, error: configError } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const t = setInterval(() => {
            setNow(new Date());
        }, 1_000);
        return () => {
            clearInterval(t);
        };
    }, []);

    const onSubmit = (e: FormEvent): void => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        void signIn(email.trim(), password)
            .then((res) => {
                if (res.error !== null) {
                    setError(res.error);
                }
            })
            .finally(() => {
                setBusy(false);
            });
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
            {/* ─── Form ───────────────────────────────────────────────────── */}
            <Box
                sx={{
                    width: { xs: '100%', md: '40%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    p: 4,
                    // Clears the notch in an installed PWA; env() is 0 on desktop.
                    pt: 'calc(16px + env(safe-area-inset-top, 0px))',
                    bgcolor: 'background.default',
                    overflow: 'auto',
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Box
                            component="img"
                            src="/silverline-logo.png"
                            alt="Silverline Construction Group"
                            sx={{ width: '100%', maxWidth: 220, height: 'auto', my: 2 }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h5" sx={{ color: TEXT_LIGHT, mb: 1 }}>
                            {getGreeting(now)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'primary.main', mb: 0.5 }}>
                            {formatLongDate(now)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                            {formatClock(now)}
                        </Typography>
                    </Box>

                    {/* A configuration failure is not something the user can
                        fix by retrying, so it is shown separately from a failed
                        sign-in and the form is disabled behind it. */}
                    {configError !== null ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {configError}
                        </Alert>
                    ) : null}

                    {error !== null ? (
                        <Typography
                            sx={{
                                color: '#e74c3c',
                                mb: 2,
                                p: 1,
                                bgcolor: 'rgba(231, 76, 60, 0.1)',
                                borderRadius: 1,
                                textAlign: 'center',
                            }}
                        >
                            {error}
                        </Typography>
                    ) : null}

                    <Box component="form" onSubmit={onSubmit}>
                        <Box sx={{ mb: 1.5 }}>
                            <Typography component="label" htmlFor="email" sx={fieldLabelSx}>
                                Email *
                            </Typography>
                            <TextField
                                id="email"
                                required
                                fullWidth
                                type="email"
                                name="email"
                                autoComplete="username"
                                autoFocus
                                placeholder="Enter Email Address"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                }}
                                sx={textFieldSx}
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
                                Password *
                            </Typography>
                            <TextField
                                id="password"
                                required
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                autoComplete="current-password"
                                placeholder="Enter Password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                }}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => {
                                                        setShowPassword((v) => !v);
                                                    }}
                                                    edge="end"
                                                    size="small"
                                                    sx={{ color: TEXT_MUTED }}
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                sx={textFieldSx}
                            />
                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={email === '' || password === '' || busy || configError !== null}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'background.default',
                                p: 1.5,
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#2980b9' },
                                '&.Mui-disabled': { bgcolor: BORDER },
                            }}
                        >
                            {busy ? <CircularProgress size={22} color="inherit" /> : 'Login'}
                        </Button>
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{ color: BORDER, textAlign: 'center', display: 'block', width: '100%', mt: 4, mb: 2 }}
                    >
                        Copyright © Silverline Construction Group {now.getFullYear()}
                    </Typography>
                </Box>
            </Box>

            {/* ─── Branding ───────────────────────────────────────────────── */}
            <Box
                sx={(theme) => ({
                    width: '60%',
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    backgroundImage: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.main} 100%)`,
                })}
            >
                <Box sx={{ textAlign: 'center', color: 'common.white', p: 4 }}>
                    <Typography variant="h3" sx={{ mb: 2, fontWeight: 300 }}>
                        Welcome to
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
                        Silverline
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.8, color: 'primary.main' }}>
                        Scheduling, Timesheets and Compliance
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
